import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

// Fix import paths

import { toast } from 'sonner';
import { Wrench, User, Briefcase, Eye, EyeOff, ArrowLeft, CheckCircle, Building2, FileText, Shield, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = window.location.origin;

// Sanitize WYSIWYG content: replace &nbsp; with regular spaces for proper word wrapping
const sanitizeHtml = (html) => {
  if (!html) return '';
  return html.replace(/&nbsp;/g, ' ');
};
const API = `${BACKEND_URL}/api`;

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  
  const defaultRole = searchParams.get('role') || 'customer';
  const [role, setRole] = useState(defaultRole);
  const [providerType, setProviderType] = useState('individual');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [siteIdentity, setSiteIdentity] = useState(null);
  
  // Legal popup states
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  const [privacyContent, setPrivacyContent] = useState('');
  const [termsAcceptedInPopup, setTermsAcceptedInPopup] = useState(false);
  const [privacyAcceptedInPopup, setPrivacyAcceptedInPopup] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    // Fetch site identity for logo
    axios.get(`${API}/cms/landing`)
      .then(res => {
        if (res.data?.site_identity) {
          setSiteIdentity(res.data.site_identity);
        }
      })
      .catch(() => {});
    
    // Fetch legal content
    Promise.all([
      axios.get(`${API}/cms/legal/terms`),
      axios.get(`${API}/cms/legal/privacy`)
    ]).then(([termsRes, privacyRes]) => {
      if (termsRes.data?.content) setTermsContent(termsRes.data.content);
      if (privacyRes.data?.content) setPrivacyContent(privacyRes.data.content);
    }).catch(() => {});
  }, []);

  // Update main checkbox when both popups are accepted
  useEffect(() => {
    if (termsAcceptedInPopup && privacyAcceptedInPopup) {
      setAcceptedTerms(true);
    }
  }, [termsAcceptedInPopup, privacyAcceptedInPopup]);

  const handleAcceptTerms = () => {
    setTermsAcceptedInPopup(true);
    setShowTermsPopup(false);
    toast.success('Terms and Conditions accepted');
  };

  const handleAcceptPrivacy = () => {
    setPrivacyAcceptedInPopup(true);
    setShowPrivacyPopup(false);
    toast.success('Privacy Policy accepted');
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptedTerms) {
      toast.error('Please read and accept the Terms of Use to continue');
      return;
    }
    
    if (!formData.phone || formData.phone.trim().length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const user = await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        role: role,
        provider_type: role === 'provider' ? providerType : undefined
      });
      
      toast.success('Account created successfully!');
      
      // Redirect based on role
      if (user.role === 'provider') {
        navigate('/provider');
      } else {
        navigate('/home');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      {/* Header */}
      <header className="p-4">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => navigate('/')}
          className="text-slate-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            {siteIdentity?.site_logo ? (
              <img 
                src={siteIdentity.site_logo.startsWith('http') 
                    ? siteIdentity.site_logo 
                    : `${BACKEND_URL}${siteIdentity.site_logo}`
                } 
                alt={siteIdentity?.site_title || 'HandyBayani'} 
                className="h-16 w-16 object-contain mx-auto mb-4 rounded-2xl"
              />
            ) : (
              <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-4 p-2">
                <img src="/handybayani-logo.png" alt="HandyBayani" className="w-12 h-12 object-contain" />
              </div>
            )}
            <h1 className="text-2xl font-bold text-slate-900 font-[Poppins]">Create Account</h1>
            <p className="text-slate-500 mt-1">Join the {siteIdentity?.site_title || 'HandyBayani'} community</p>
          </div>

          {/* Role Selection */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <button
              type="button"
              className={`p-4 rounded-xl border-2 transition-all ${
                role === 'customer' 
                  ? 'border-slate-900 bg-slate-50' 
                  : 'border-stone-200 hover:border-stone-300'
              }`}
              onClick={() => setRole('customer')}
              data-testid="role-customer-btn"
            >
              <User className={`w-6 h-6 mx-auto mb-2 ${role === 'customer' ? 'text-slate-900' : 'text-slate-400'}`} />
              <p className={`font-medium ${role === 'customer' ? 'text-slate-900' : 'text-slate-500'}`}>Customer</p>
              <p className="text-xs text-slate-400 mt-0.5">Book services</p>
            </button>
            
            <button
              type="button"
              className={`p-4 rounded-xl border-2 transition-all ${
                role === 'provider' 
                  ? 'border-slate-900 bg-slate-50' 
                  : 'border-stone-200 hover:border-stone-300'
              }`}
              onClick={() => setRole('provider')}
              data-testid="role-provider-btn"
            >
              <Briefcase className={`w-6 h-6 mx-auto mb-2 ${role === 'provider' ? 'text-slate-900' : 'text-slate-400'}`} />
              <p className={`font-medium ${role === 'provider' ? 'text-slate-900' : 'text-slate-500'}`}>Provider</p>
              <p className="text-xs text-slate-400 mt-0.5">Offer services</p>
            </button>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder="Juan dela Cruz"
                value={formData.name}
                onChange={handleChange}
                className="h-12 mt-1.5"
                required
                data-testid="register-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                className="h-12 mt-1.5"
                required
                data-testid="register-email-input"
              />
            </div>
            
            <div>
              <Label htmlFor="phone">Phone Number <span className="text-red-500">*</span></Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="09171234567"
                value={formData.phone}
                onChange={handleChange}
                className="h-12 mt-1.5"
                required
                data-testid="register-phone-input"
              />
            </div>
            
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={handleChange}
                  className="h-12 pr-12"
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <div>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="h-12 mt-1.5"
                required
                data-testid="register-confirm-password-input"
              />
            </div>

            {role === 'provider' && (
              <>
                {/* Provider Type Selection */}
                <div className="space-y-2">
                  <Label>Provider Type</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button type="button"
                      className={`p-3 rounded-xl border-2 transition-all text-left ${providerType === 'individual' ? 'border-slate-900 bg-slate-50' : 'border-stone-200 hover:border-stone-300'}`}
                      onClick={() => setProviderType('individual')}
                      data-testid="provider-type-individual"
                    >
                      <User className={`w-5 h-5 mb-1 ${providerType === 'individual' ? 'text-slate-900' : 'text-slate-400'}`} />
                      <p className={`font-medium text-sm ${providerType === 'individual' ? 'text-slate-900' : 'text-slate-500'}`}>Individual</p>
                      <p className="text-xs text-slate-400">Freelancer / Solo</p>
                    </button>
                    <button type="button"
                      className={`p-3 rounded-xl border-2 transition-all text-left ${providerType === 'company' ? 'border-slate-900 bg-slate-50' : 'border-stone-200 hover:border-stone-300'}`}
                      onClick={() => setProviderType('company')}
                      data-testid="provider-type-company"
                    >
                      <Building2 className={`w-5 h-5 mb-1 ${providerType === 'company' ? 'text-slate-900' : 'text-slate-400'}`} />
                      <p className={`font-medium text-sm ${providerType === 'company' ? 'text-slate-900' : 'text-slate-500'}`}>Company</p>
                      <p className="text-xs text-slate-400">Business / Team</p>
                    </button>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800">
                        {providerType === 'company' ? 'Company Requirements' : 'Provider Verification'}
                      </p>
                      <p className="text-amber-700 mt-1">
                        {providerType === 'company'
                          ? 'You\'ll need to upload DTI Registration and Business Permit after registration.'
                          : 'You\'ll need to upload your ID after registration to verify your identity.'}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Terms of Use Checkbox */}
            <div className="p-4 bg-stone-50 rounded-xl border border-stone-200 space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="terms"
                  checked={acceptedTerms}
                  onChange={(e) => {
                    if (e.target.checked && (!termsAcceptedInPopup || !privacyAcceptedInPopup)) {
                      toast.error('Please read and accept both Terms and Privacy Policy using the links below');
                      return;
                    }
                    setAcceptedTerms(e.target.checked);
                  }}
                  className="w-5 h-5 rounded border-slate-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                  data-testid="terms-checkbox"
                />
                <label htmlFor="terms" className="text-sm text-slate-600 cursor-pointer">
                  I have read, understood and accept the Terms and Privacy Policy
                </label>
              </div>
              
              <div className="flex items-center gap-4 pl-8">
                <button
                  type="button"
                  onClick={() => setShowTermsPopup(true)}
                  className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                    termsAcceptedInPopup ? 'text-green-600' : 'text-amber-600 hover:text-amber-700'
                  }`}
                  data-testid="open-terms-popup"
                >
                  <FileText className="w-4 h-4" />
                  Terms of Use
                  {termsAcceptedInPopup && <CheckCircle className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPrivacyPopup(true)}
                  className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                    privacyAcceptedInPopup ? 'text-green-600' : 'text-amber-600 hover:text-amber-700'
                  }`}
                  data-testid="open-privacy-popup"
                >
                  <Shield className="w-4 h-4" />
                  Privacy Policy
                  {privacyAcceptedInPopup && <CheckCircle className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 disabled:cursor-not-allowed"
              disabled={isLoading || !acceptedTerms}
              data-testid="register-submit-btn"
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          {/* Login Link */}
          <p className="text-center mt-6 text-slate-600">
            Already have an account?{' '}
            <Link to="/login" className="text-amber-600 hover:underline font-medium" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Terms Popup */}
      <Dialog open={showTermsPopup} onOpenChange={setShowTermsPopup}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-600" />
              Terms and Conditions
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2 mt-4 pb-6">
            <div 
              className="wysiwyg-content prose prose-sm max-w-none prose-headings:text-slate-900 prose-p:text-slate-600"
              style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(termsContent) }}
            />
          </div>
          <div className="sticky bottom-0 bg-white border-t pt-4 space-y-3 -mx-6 px-6 pb-2 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="accept-terms-popup"
                checked={termsAcceptedInPopup}
                onChange={(e) => setTermsAcceptedInPopup(e.target.checked)}
                className="w-5 h-5 rounded border-slate-300 text-amber-600 focus:ring-amber-500 shrink-0"
                data-testid="accept-terms-popup-checkbox"
              />
              <label htmlFor="accept-terms-popup" className="text-sm text-slate-700 cursor-pointer font-medium">
                I have read, understand and accept the Terms and Conditions
              </label>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowTermsPopup(false)}
                className="flex-1"
              >
                Close
              </Button>
              <Button
                onClick={handleAcceptTerms}
                disabled={!termsAcceptedInPopup}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-900 disabled:opacity-50"
                data-testid="accept-terms-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Accept Terms
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Privacy Popup */}
      <Dialog open={showPrivacyPopup} onOpenChange={setShowPrivacyPopup}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              Privacy Policy
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2 mt-4 pb-6">
            <div 
              className="wysiwyg-content prose prose-sm max-w-none prose-headings:text-slate-900 prose-p:text-slate-600"
              style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(privacyContent) }}
            />
          </div>
          <div className="sticky bottom-0 bg-white border-t pt-4 space-y-3 -mx-6 px-6 pb-2 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="accept-privacy-popup"
                checked={privacyAcceptedInPopup}
                onChange={(e) => setPrivacyAcceptedInPopup(e.target.checked)}
                className="w-5 h-5 rounded border-slate-300 text-amber-600 focus:ring-amber-500 shrink-0"
                data-testid="accept-privacy-popup-checkbox"
              />
              <label htmlFor="accept-privacy-popup" className="text-sm text-slate-700 cursor-pointer font-medium">
                I have read, understand and accept the Privacy Policy
              </label>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowPrivacyPopup(false)}
                className="flex-1"
              >
                Close
              </Button>
              <Button
                onClick={handleAcceptPrivacy}
                disabled={!privacyAcceptedInPopup}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-900 disabled:opacity-50"
                data-testid="accept-privacy-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Accept Privacy Policy
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
